import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Calendar, Clock, Users, Phone } from 'lucide-react';

// Define the Reservation interface for this component
interface Reservation {
  id: string;
  bookedId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  date: string;
  time: string;
  guestNo: number;
  specialRequests?: string;
  status: 'pending' | 'accepted' | 'cancelled' | 'completed' | 'incompleted' | 'rejected';
  createdAt: string;
  tableId?: string;
  tableNo: number;
}

interface CalendarDateDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly date: string;
  readonly reservations: Reservation[];
  readonly onReservationClick: (reservation: Reservation) => void;
}

const CalendarDateDialog = React.memo(function CalendarDateDialog({ 
  open, 
  onClose, 
  date, 
  reservations,
  onReservationClick 
}: CalendarDateDialogProps) {
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Selected Date';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Render optimization: Only log when dialog is actually open
  // console.log('📅 CalendarDateDialog render:', { open, date, reservationsCount: reservations?.length || 0 });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className=" bg-gray-100 dark:bg-gray-900 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Reservations for {formatDate(date)}
          </DialogTitle>
          <DialogDescription>
            {reservations?.length === 1 
              ? '1 reservation found' 
              : `${reservations?.length || 0} reservations found`}
            {reservations && reservations.length > 0 && ' - Click on any reservation to view full details'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {!reservations || reservations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No reservations found for this date.</p>
              <p className="text-xs mt-2 opacity-75">
                Try navigating to a different month using the calendar controls above, or create a new reservation for this date.
              </p>
            </div>
          ) : (
            reservations.map((reservation) => (
              <button 
                key={`dialog-${reservation.id}-${reservation.bookedId}`}
                className="w-full border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer text-left"
                onClick={() => {
                  onReservationClick(reservation);
                  onClose();
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center space-x-3">
                      <h4 className="font-semibold text-lg">{reservation.customerName}</h4>
                      <Badge className={getStatusColor(reservation.status)}>
                        {reservation.status.charAt(0).toUpperCase() + reservation.status.slice(1)}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4" />
                        <span>{reservation.time}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4" />
                        <span>{reservation.guestNo} guests</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4" />
                        <span>{reservation.customerPhone}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs">#{reservation.bookedId}</span>
                      </div>
                    </div>

                    {reservation.specialRequests && (
                      <div className="bg-muted p-2 rounded text-sm">
                        <strong>Special Requests:</strong> {reservation.specialRequests}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
        
        <div className="flex justify-end mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
});

export default CalendarDateDialog;